<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useRoute, useRouter } from "vue-router"
import { useToast } from "primevue/usetoast"
import { useBuildingStore } from "@core/stores/building-v2.store"
import {
    estRefusDacces,
    fetchErrorRedirectToast,
    isBuildingSidebarRoute,
    restrictedAccessRedirectToast,
} from "@core/router/restrictedAccessNavigation"

const route = useRoute()
const router = useRouter()
const toast = useToast()
const buildingStore = useBuildingStore()
const { building, hasErrorBuilding, errorBuilding } = storeToRefs(buildingStore)

/**
 * Dernier `_id_building` pour lequel le couple fetchBuilding + fetchBuildingSources
 * a terminé. `null` = chargement en cours ou pas encore de cycle pour l’URL actuelle
 * (évite un flash de sous-page avant que `isFetchingBuilding` passe à true).
 */
const shellFetchSettledForRouteId = ref<string | null>(null)

/** Évite un flash de sous-page pendant la navigation vers l’accueil après erreur API. */
const isNavigatingHomeAfterBuildingFetchError = ref(false)

watch(
    () => route.params._id_building,
    async (newId) => {
        if (!newId || typeof newId !== "string") {
            shellFetchSettledForRouteId.value = null
            return
        }
        shellFetchSettledForRouteId.value = null
        try {
            await buildingStore.fetchBuilding(newId)
            if (buildingStore.hasErrorBuilding) {
                if (
                    route.params._id_building === newId &&
                    isBuildingSidebarRoute(router.currentRoute.value)
                ) {
                    isNavigatingHomeAfterBuildingFetchError.value = true
                    try {
                        // Un 5xx n'est pas un refus d'accès : l'annoncer comme tel
                        // accuse l'utilisateur d'un problème de droits qu'il n'a pas.
                        const refus = estRefusDacces(errorBuilding.value)
                        const textes = refus
                            ? restrictedAccessRedirectToast.building
                            : fetchErrorRedirectToast.building
                        await router.push({ name: "Home", query: {} })
                        toast.add({
                            group: "notifications",
                            severity: refus ? "warn" : "error",
                            summary: textes.summary,
                            detail: textes.detail,
                            life: 5000,
                        })
                    } finally {
                        isNavigatingHomeAfterBuildingFetchError.value = false
                    }
                }
                return
            }
            await buildingStore.fetchBuildingSources()
        } finally {
            shellFetchSettledForRouteId.value = newId
        }
    },
    { immediate: true }
)

const routeBuildingId = computed(
    () => route.params._id_building as string | undefined
)

/** Le store reflète le bâtiment demandé par l’URL. */
const isBuildingAlignedForRoute = computed(() => {
    const id = routeBuildingId.value
    if (!id) return false
    return building.value?._id_building === id
})

/**
 * Retarde le montage des sous-pages tant qu’on attend encore le bon bâtiment sans erreur.
 * Après erreur (403/404, etc.) ou après un fetch terminé sans alignement, les enfants montent
 * pour pouvoir afficher un état via le store.
 */
const shouldDeferChildForBuildingFetch = computed(() => {
    const id = routeBuildingId.value
    if (!id) return false
    if (isNavigatingHomeAfterBuildingFetchError.value) return true
    if (isBuildingAlignedForRoute.value) return false
    if (hasErrorBuilding.value) return false
    if (shellFetchSettledForRouteId.value === id) return false
    return true
})
</script>

<template>
    <LoadingApp v-if="shouldDeferChildForBuildingFetch" class="page" />
    <template v-else-if="routeBuildingId">
        <router-view />
        <!-- Même dialogue que le dashboard (sidebar bâtiment : suppression). -->
        <HomeBuildingDialogs />
    </template>
</template>
